import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Bot, User, CheckCircle2, ArrowRight } from 'lucide-react';
import { PLAYS } from './playsData';
import './plays.css';

const STEP_MS = 1100;
const HOLD_MS = 3200;

const Wave = () => (
  <span className="vx-play-wave" aria-hidden="true">{[0, 1, 2, 3, 4].map(i => <i key={i} style={{ animationDelay: `${i * 0.12}s` }} />)}</span>
);

const Line = ({ line }) => {
  if (line.who === 'system') return <div className="vx-play-system">{line.text}</div>;
  const agent = line.who === 'agent';
  return (
    <div className={`vx-play-line ${agent ? 'is-agent' : 'is-lead'}`}>
      <span className="vx-play-avatar">{agent ? <Bot size={13} /> : <User size={13} />}</span>
      <p>{line.voice && <Wave />}{line.text}</p>
    </div>
  );
};

const Stage = ({ play, shown, done }) => (
  <div className="vx-play-stage" data-testid="plays-stage">
    <div className="vx-play-stage-top">
      <span className="vx-play-stage-title"><play.icon size={15} />{play.title}</span>
      <span className="vx-play-stage-channels">{play.channels.map(c => <em key={c}>{c}</em>)}</span>
    </div>
    <div className="vx-play-convo" data-testid="plays-conversation">
      <AnimatePresence initial={false}>
        {play.script.slice(0, shown).map((line, i) => (
          <motion.div key={`${play.id}-${i}`} initial={{ opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
            <Line line={line} />
          </motion.div>
        ))}
      </AnimatePresence>
      {!done && shown < play.script.length && <div className="vx-play-typing" aria-hidden="true"><i /><i /><i /></div>}
      <AnimatePresence>
        {done && (
          <motion.div className="vx-play-result" data-testid="plays-result" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <CheckCircle2 size={16} />{play.result}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    <ol className="vx-play-flow" data-testid="plays-flow">
      {play.flow.map((step, i) => (
        <li key={step} className={shown > i + 1 || done ? 'is-on' : ''}>
          <span>{['Trigger', 'AI does', 'Result'][i]}</span>{step}
        </li>
      ))}
    </ol>
  </div>
);

export const PlaysSection = () => {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const [auto, setAuto] = useState(true);
  const play = PLAYS[active];
  const done = shown >= play.script.length;

  useEffect(() => { setShown(reduce ? PLAYS[active].script.length : 0); }, [active, reduce]);
  useEffect(() => {
    if (done) {
      if (!auto || reduce) return undefined;
      const t = setTimeout(() => setActive(a => (a + 1) % PLAYS.length), HOLD_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setShown(s => s + 1), shown === 0 ? 350 : STEP_MS);
    return () => clearTimeout(t);
  }, [shown, done, auto, reduce]);

  const pick = (i) => { setAuto(false); setActive(i); };

  return (
    <section id="plays" className="vx-plays" data-testid="home-plays-section">
      <div className="hm-container">
        <motion.header className="vx-plays-head" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="vx-plays-eyebrow"><span>01</span>PUT VOXINITY TO WORK</p>
          <h2 data-testid="plays-heading">Seven offers. <em>Ready to sell.</em></h2>
          <p className="vx-plays-sub">Pick a play. Watch the AI run it — on calls, texts and DMs.</p>
        </motion.header>
        <div className="vx-plays-grid">
          <div className="vx-plays-list" role="tablist" aria-label="Agency plays">
            {PLAYS.map((p, i) => (
              <button key={p.id} type="button" role="tab" aria-selected={i === active} onClick={() => pick(i)}
                className={`vx-play-tab${i === active ? ' is-active' : ''}`} data-testid={`play-tab-${p.id}`}>
                <span className="vx-play-tab-icon"><p.icon size={17} /></span>
                <span className="vx-play-tab-text"><strong>{p.title}</strong><small>{p.tagline}</small></span>
                {i === active && auto && !reduce && <span key={`bar-${active}`} className="vx-play-tab-progress" style={{ animationDuration: `${350 + STEP_MS * (p.script.length - 1) + HOLD_MS}ms` }} />}
                <ArrowRight size={15} className="vx-play-tab-arrow" />
              </button>
            ))}
          </div>
          <Stage play={play} shown={shown} done={done} />
        </div>
        <p className="vx-footnote" data-testid="plays-footnote">Scripted example conversations. Channels depend on each client’s connected accounts.</p>
      </div>
    </section>
  );
};
